import { Fragment, useMemo, useState } from "react";
import { STATUS, STATUS_ORDER, PRIORITY } from "../constants";
import { StatusBadge, TaskStatusBadge, PriorityBadge, ProgressBar, Avatar, RiskBadge } from "./ui";
import { isOverdue, fmtDate, fmtStampDate } from "../utils";
import TasksEmailModal from "./TasksEmailModal";
import Icon from "./Icon";

const RISK_RANK = { high: 3, medium: 2, low: 1 };
// Sortable columns: [label, key|null]. null = not sortable.
const COLS = [
  ["ID", "code"],
  ["Tarea", "title"], ["Riesgo", "risk"], ["Estado", "status"],
  ["Prioridad", "priority"], ["Avance", "progress_pct"], ["Responsables", null], ["PMO", null],
  ["Creado por", "creator"], ["Creada", "created_at"], ["Compromiso", "due_date"],
];
// checkbox + COLS
const TOTAL_COLS = COLS.length + 1;

function sortVal(t, key) {
  switch (key) {
    case "code": return (t.code || "").toLowerCase();
    case "title": return (t.title || "").toLowerCase();
    case "risk": return RISK_RANK[t.risk] || 0;
    case "status": return STATUS_ORDER.indexOf(t.status);
    case "priority": return PRIORITY[t.priority]?.rank || 0;
    case "progress_pct": return t.progress_pct || 0;
    case "creator": return (t.creator?.full_name || "").toLowerCase();
    case "created_at": return t.created_at ? new Date(t.created_at).getTime() : 0;
    case "due_date": return t.due_date ? new Date(t.due_date).getTime() : Infinity;
    default: return 0;
  }
}

export default function TaskListView({ tasks, onOpen }) {
  const [selected, setSelected] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [hideDone, setHideDone] = useState(false);
  // Bitácora indentada bajo cada tarea (estilo árbol OpenProject). Expandidas
  // por defecto; el chevron de la fila padre colapsa/expande sus entradas.
  const [showProgress, setShowProgress] = useState(true);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggleCollapse = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const sortBy = (key) => {
    if (!key) return;
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  const rows = useMemo(() => {
    let r = hideDone ? tasks.filter((t) => t.status !== "completed") : [...tasks];
    if (sort.key) {
      const m = sort.dir === "asc" ? 1 : -1;
      r = [...r].sort((a, b) => {
        const va = sortVal(a, sort.key), vb = sortVal(b, sort.key);
        return va < vb ? -m : va > vb ? m : 0;
      });
    }
    return r;
  }, [tasks, sort, hideDone]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = rows.length > 0 && rows.every((t) => selected.includes(t.id));
  const toggleAll = () => setSelected(allSelected ? [] : rows.map((t) => t.id));
  const selectedTasks = tasks.filter((t) => selected.includes(t.id));
  const arrow = (key) => (sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "");

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} />
            Ocultar completados
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600" title="Muestra cada avance de la bitácora indentado bajo su tarea">
            <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={showProgress} onChange={(e) => setShowProgress(e.target.checked)} />
            Mostrar bitácora
          </label>
        </div>
        <span className="text-xs text-slate-400">{rows.length} tarea(s) · clic en una columna para ordenar</span>
      </div>
      {selected.length > 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-2 text-sm">
          <span className="font-medium text-brand-700">{selected.length} seleccionada(s)</span>
          <button className="btn-primary h-8" onClick={() => setEmailOpen(true)}><Icon name="mail" size={15} /> Enviar por correo</button>
          <button className="btn-ghost h-8" onClick={() => setSelected([])}>Limpiar</button>
        </div>
      )}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2"><input type="checkbox" className="h-4 w-4 accent-brand-600" checked={allSelected} onChange={toggleAll} /></th>
              {COLS.map(([h, key]) => (
                <th key={h} onClick={() => sortBy(key)}
                  className={`px-3 py-2 text-left font-semibold ${key ? "cursor-pointer select-none hover:text-brand-600" : ""} ${sort.key === key ? "text-brand-600" : ""}`}>
                  {h}{arrow(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const briefs = showProgress ? (t.progress_brief || []) : [];
              const hasBriefs = briefs.length > 0;
              const isCollapsed = collapsed.has(t.id);
              return (
              <Fragment key={t.id}>
              <tr className={`border-t border-slate-100 hover:bg-slate-50 ${selected.includes(t.id) ? "bg-brand-50/40" : ""}`}>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={selected.includes(t.id)} onChange={() => toggle(t.id)} />
                </td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-brand-600">{t.code || `#${t.id}`}</td>
                <td className="cursor-pointer px-3 py-2 font-medium" onClick={() => onOpen(t)}>
                  <span className="flex items-start gap-1">
                    {hasBriefs && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); toggleCollapse(t.id); }}
                        aria-label={isCollapsed ? "Expandir bitácora" : "Colapsar bitácora"}
                        className="mt-0.5 shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                        <Icon name="chevron-down" size={12} className={`transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                      </button>
                    )}
                    <span className="uppercase">
                      {t.title}
                      {t.project_name && <div className="flex items-center gap-1 text-[11px] font-normal text-violet-600"><Icon name="folder" size={11} /> {t.project_name}</div>}
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2"><RiskBadge risk={t.risk} compact /></td>
                <td className="px-3 py-2"><TaskStatusBadge task={t} /></td>
                <td className="px-3 py-2"><PriorityBadge priority={t.priority} /></td>
                <td className="px-3 py-2"><div className="flex items-center gap-2"><div className="w-16"><ProgressBar value={t.progress_pct} /></div><span className="text-xs text-slate-400">{t.progress_pct}%</span></div></td>
                <td className="px-3 py-2"><div className="flex -space-x-1">{t.assignees?.slice(0, 3).map((a) => <Avatar key={a.id} name={a.full_name} src={a.avatar_url} size={22} />)}</div></td>
                <td className="px-3 py-2"><div className="flex -space-x-1">{t.pmo?.slice(0, 2).map((p) => <Avatar key={p.id} name={p.full_name} src={p.avatar_url} size={22} />)}</div></td>
                <td className="px-3 py-2">
                  {t.creator ? (
                    <div className="flex items-center gap-1.5">
                      <Avatar name={t.creator.full_name} src={t.creator.avatar_url} size={20} />
                      <span className="truncate text-xs text-slate-500">{t.creator.full_name}</span>
                    </div>
                  ) : <span className="text-xs text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-400">{fmtStampDate(t.created_at)}</td>
                <td className={`px-3 py-2 text-xs ${isOverdue(t) ? "font-semibold text-rose-600" : "text-slate-500"}`}><span className="inline-flex items-center gap-1">{isOverdue(t) && <Icon name="alert" size={12} />}{fmtDate(t.due_date)}</span></td>
              </tr>
              {/* Bitácora: una sub-fila por avance, indentada bajo la tarea con
                  el mismo título en tamaño menor + autor + fecha + minutos. */}
              {hasBriefs && !isCollapsed && briefs.map((p) => (
                <tr key={`p-${t.id}-${p.id}`} className="cursor-pointer border-t border-slate-50 bg-indigo-50/30 hover:bg-indigo-50/60"
                  onClick={() => { sessionStorage.setItem("tf:open-tab", "bitacora"); onOpen(t); }}
                  title="Ver el Registro de Avances">
                  <td className="px-3 py-1" />
                  <td className="px-3 py-1" />
                  <td className="px-3 py-1">
                    <div className="flex items-center gap-1.5 pl-7 text-[11px] text-slate-500">
                      <Icon name="history" size={11} className="shrink-0 text-indigo-400" />
                      <span className="truncate uppercase">{t.title}</span>
                    </div>
                  </td>
                  <td colSpan={TOTAL_COLS - 3} className="px-3 py-1">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[11px]">
                      <span className="font-medium text-slate-600">{p.author_name || "—"}</span>
                      <span className="text-slate-400">{fmtDate(p.work_date)}</span>
                      <span className={`rounded-tag px-1.5 py-0.5 text-[11px] font-semibold ${p.minutes > 0 ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-400"}`}>
                        {p.minutes > 0 ? `${p.minutes} min` : "sin tiempo"}
                      </span>
                      {p.description && <span className="max-w-[320px] truncate text-slate-400" title={p.description}>{p.description}</span>}
                    </div>
                  </td>
                </tr>
              ))}
              </Fragment>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={TOTAL_COLS} className="px-3 py-10 text-center text-slate-400">Sin tareas.</td></tr>}
          </tbody>
        </table>
      </div>
      <TasksEmailModal open={emailOpen} onClose={() => setEmailOpen(false)} tasks={selectedTasks} onSent={() => setSelected([])} />
    </div>
  );
}

// Build Gantt groups (by assignee) from a flat task list.
export function groupForTimeline(tasks) {
  const groups = {};
  tasks.forEach((t) => {
    const bar = { id: t.id, code: t.code, title: t.title, status: t.status, priority: t.priority, progress_pct: t.progress_pct, start_date: t.start_date, due_date: t.due_date, assignees: t.assignees || [] };
    const list = t.assignees?.length ? t.assignees : [{ id: 0, full_name: "Sin asignar" }];
    list.forEach((u) => {
      groups[u.id] = groups[u.id] || { user_id: u.id, full_name: u.full_name, bars: [] };
      groups[u.id].bars.push(bar);
    });
  });
  return Object.values(groups).sort((a, b) => a.full_name.localeCompare(b.full_name));
}

// Project timeline: cada tarea aparece UNA vez, AGRUPADA POR PROYECTO (cada
// proyecto es una fila-grupo que el Gantt encierra con líneas horizontales).
// Por defecto oculta las tareas completadas (timeline general); con
// includeCompleted=true las muestra todas (timeline DENTRO de un proyecto).
export function groupForProjectTimeline(tasks, filterAssigneeId, includeCompleted = false) {
  let list = includeCompleted ? tasks : tasks.filter((t) => t.status !== "completed");
  if (filterAssigneeId) {
    list = list.filter((t) => t.assignees?.some((a) => String(a.id) === String(filterAssigneeId)));
  }
  const groups = {};
  list.forEach((t) => {
    const key = t.project_id || "__none__";
    groups[key] = groups[key] || {
      user_id: `__project_${key}__`,
      full_name: t.project_name || "Sin proyecto",
      is_project: true,
      bars: [],
    };
    groups[key].bars.push({
      id: t.id, code: t.code, title: t.title, status: t.status, priority: t.priority,
      progress_pct: t.progress_pct, start_date: t.start_date, due_date: t.due_date,
      assignees: t.assignees || [],
      depends_on_ids: t.depends_on_ids || t.dependencies?.map((d) => d.depends_on_id) || [],
    });
  });
  return Object.values(groups).sort((a, b) => a.full_name.localeCompare(b.full_name));
}

// Project timeline agrupado POR FASE/HITO (Fase 3.3): cada fase es una banda
// con sus tareas; "Sin fase" queda al final. `phaseOrder` (mapa phase_id→pos)
// respeta el orden definido en la pestaña Fases.
export function groupForPhaseTimeline(tasks, includeCompleted = false, phaseOrder = {}) {
  const list = includeCompleted ? tasks : tasks.filter((t) => t.status !== "completed");
  const groups = {};
  list.forEach((t) => {
    const key = t.phase_id || "__nophase__";
    groups[key] = groups[key] || {
      user_id: `__phase_${key}__`,
      full_name: t.phase_id ? (t.phase_name || "Fase") : "Sin fase",
      is_phase: true,
      _pos: t.phase_id ? (phaseOrder[t.phase_id] ?? 9998) : 9999,
      bars: [],
    };
    groups[key].bars.push({
      id: t.id, code: t.code, title: t.title, status: t.status, priority: t.priority,
      progress_pct: t.progress_pct, start_date: t.start_date, due_date: t.due_date,
      assignees: t.assignees || [],
      depends_on_ids: t.depends_on_ids || t.dependencies?.map((d) => d.depends_on_id) || [],
    });
  });
  return Object.values(groups).sort((a, b) => a._pos - b._pos || a.full_name.localeCompare(b.full_name));
}
